import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell, CheckCheck, MapPin, FileText, Sparkles, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Which Admin Panel tab a notification deep-links to when tapped.
const TYPE_TAB = {
  admin_venue_application_submitted: 'venue-apps',
  venue_auto_live: 'venue-apps',
  admin_venue_stale_hold: 'venue-apps',
  admin_host_application_submitted: 'applications',
};

// Per-type accent icon for the feed row.
const TYPE_ICON = {
  admin_venue_application_submitted: MapPin,
  venue_auto_live: Sparkles,
  admin_venue_stale_hold: AlertTriangle,
  admin_host_application_submitted: FileText,
};

const relativeTime = (value) => {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(then);
};

export const AdminNotificationsBell = ({ onSelectTab }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/summary?scope=account`, { headers: authHeaders() });
      setUnread(res.data?.unread_count || 0);
    } catch (err) {
      // Silent — the bell just won't show a badge.
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/history?scope=account`, { headers: authHeaders() });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    const timer = setInterval(fetchSummary, 30000);
    return () => clearInterval(timer);
  }, [fetchSummary]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchHistory();
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { headers: authHeaders() });
    } catch (err) {
      // Non-blocking.
    }
  };

  const handleItemClick = async (item) => {
    if (!item.is_read) {
      setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
      await markRead(item.id);
    }
    const tab = TYPE_TAB[item.notification_type || item.type];
    if (tab && onSelectTab) onSelectTab(tab);
    setOpen(false);
  };

  const markAllRead = async () => {
    const unreadItems = items.filter((n) => !n.is_read);
    if (!unreadItems.length) return;
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnread(0);
    await Promise.all(unreadItems.map((n) => markRead(n.id)));
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={toggleOpen}
        data-testid="admin-notifications-bell-button"
        aria-label="Notifications"
        style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: 4, display: 'flex' }}
      >
        <Bell size={22} color="#111" />
        {unread > 0 ? (
          <span
            data-testid="admin-notifications-unread-badge"
            style={{ position: 'absolute', top: -2, right: -4, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 9999, background: '#DD0608', color: '#FFF', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          data-testid="admin-notifications-dropdown"
          style={{
            position: 'fixed',
            right: 8,
            top: 70,
            width: 360,
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: 440,
            overflowY: 'auto',
            background: '#FFF',
            border: '1px solid #E7E7E7',
            borderRadius: 16,
            boxShadow: '0 16px 48px rgba(0,0,0,0.16)',
            zIndex: 9999,
            fontFamily: "'Manrope', sans-serif",
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #F0F0F0', position: 'sticky', top: 0, background: '#FFF', borderRadius: '16px 16px 0 0' }}>
            <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: '#111' }}>Notifications</span>
            {items.some((n) => !n.is_read) ? (
              <button
                onClick={markAllRead}
                data-testid="admin-notifications-mark-all-read"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: '#555', fontSize: 12, fontWeight: 600 }}
              >
                <CheckCheck size={14} /> Mark all read
              </button>
            ) : null}
          </div>

          {items.length === 0 ? (
            <div data-testid="admin-notifications-empty" style={{ padding: '32px 18px', textAlign: 'center', color: '#999', fontSize: 13 }}>
              You&apos;re all caught up. New venue &amp; host activity will show here.
            </div>
          ) : (
            items.map((item) => {
              const Icon = TYPE_ICON[item.notification_type || item.type] || Bell;
              return (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  data-testid={`admin-notification-item-${item.id}`}
                  style={{ width: '100%', textAlign: 'left', display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 16px', border: 'none', borderBottom: '1px solid #F4F4F4', cursor: 'pointer', background: item.is_read ? '#FFF' : '#FBF4F4' }}
                >
                  <span style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 9, background: '#F2EBEB', color: '#C2410C', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                    <Icon size={15} strokeWidth={2.2} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{item.title}</span>
                      {!item.is_read ? <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#DD0608', flexShrink: 0 }} /> : null}
                    </span>
                    <span style={{ display: 'block', fontSize: 12.5, color: '#555', marginTop: 2, lineHeight: 1.35 }}>{item.message}</span>
                    <span style={{ display: 'block', fontSize: 11, color: '#999', marginTop: 4 }}>{relativeTime(item.created_at)}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
};

export default AdminNotificationsBell;
